/* eslint-disable */
import React from 'react';
import SEO from './SEO';
import './styles/styles.css';

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: 'Bins Storage – Student Storage',
  description:
    'Student storage for SF Bay Area and Tacoma colleges. We pick up at your dorm, store your stuff safely, and deliver it back for fall.',
  url: 'https://www.bins-storage.com/students',
  areaServed: [
    { '@type': 'AdministrativeArea', name: 'San Francisco Bay Area' },
    { '@type': 'City', name: 'Tacoma' },
  ],
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.bins-storage.com/' },
    { '@type': 'ListItem', position: 2, name: 'Student Storage', item: 'https://www.bins-storage.com/students' },
  ],
};

const studentFaqs = [
  {
    q: 'Can you pick up from a dorm?',
    a: "Yes – we pick up at dorms, apartments, and campus buildings. Just let us know the building during signup and we'll coordinate.",
  },
  {
    q: 'Do you offer group discounts for roommates?',
    a: "If you and roommates want to split a larger unit, you can share the cost directly – it's often cheaper than each renting separately. Contact us and we'll help you set it up.",
  },
  {
    q: 'Can I get items out of storage mid-summer if I need them?',
    a: 'Yes – schedule a delivery any time through the app.',
  },
  {
    q: 'What if my move-in date for fall changes?',
    a: 'Reschedule your delivery in the app. No fees for most changes.',
  },
  {
    q: 'Is my stuff insured?',
    a: 'Yes – every facility in our network includes insurance. Details during signup.',
  },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: studentFaqs.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
};

const combinedSchema = [localBusinessSchema, breadcrumbSchema, faqSchema];

// ─── Styles ───────────────────────────────────────────────────────────────────

const heroStyle = {
  backgroundColor: '#7B1FA2',
  padding: '4rem 2rem 3rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
};

const heroHeadingStyle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: 'clamp(2rem, 5vw, 3.5rem)',
  fontWeight: 700,
  color: '#FFF',
  margin: '0 0 1rem',
};

const heroSubStyle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '1.2rem',
  color: 'rgba(255,255,255,0.88)',
  maxWidth: 640,
  lineHeight: 1.6,
  margin: '0 0 0.5rem',
};

const ctaRow = {
  display: 'flex',
  gap: '1rem',
  flexWrap: 'wrap',
  justifyContent: 'center',
  margin: '1.5rem 0 0.75rem',
};

const ctaPrimary = {
  display: 'inline-block',
  backgroundColor: '#FFF',
  color: '#7B1FA2',
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 700,
  fontSize: '1.1rem',
  padding: '14px 32px',
  borderRadius: 999,
  textDecoration: 'none',
};

const ctaSecondary = {
  display: 'inline-block',
  backgroundColor: 'transparent',
  color: '#FFF',
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 700,
  fontSize: '1.1rem',
  padding: '14px 32px',
  borderRadius: 999,
  textDecoration: 'none',
  border: '2px solid rgba(255,255,255,0.6)',
};

const trustLine = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '0.9rem',
  color: 'rgba(255,255,255,0.75)',
  margin: '0.5rem 0 0',
};

const sectionStyle = {
  padding: '3.5rem 2rem',
  maxWidth: 900,
  margin: '0 auto',
};

const sectionHeadingStyle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '2rem',
  fontWeight: 700,
  color: '#261136',
  marginBottom: '0.5rem',
  textAlign: 'center',
};

const sectionSubStyle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '1.05rem',
  color: '#555',
  textAlign: 'center',
  maxWidth: 640,
  margin: '0 auto 2.5rem',
  lineHeight: 1.65,
};

const cardsGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '1.5rem',
};

const card = {
  backgroundColor: '#F9F6FC',
  borderRadius: 12,
  padding: '1.75rem 1.5rem',
  textAlign: 'center',
};

const cardTitle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '1.1rem',
  fontWeight: 700,
  color: '#261136',
  marginBottom: '0.5rem',
};

const cardBody = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '0.95rem',
  color: '#555',
  lineHeight: 1.6,
};

const stepNumber = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '2.5rem',
  fontWeight: 800,
  color: '#7B1FA2',
  marginBottom: '0.5rem',
};

const schoolsSection = {
  backgroundColor: '#F9F6FC',
  padding: '3.5rem 2rem',
};

const schoolsInner = {
  maxWidth: 800,
  margin: '0 auto',
};

const schoolGroup = {
  marginBottom: '2rem',
};

const schoolGroupHeading = {
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 700,
  fontSize: '1.1rem',
  color: '#7B1FA2',
  marginBottom: '0.75rem',
};

const schoolList = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  gap: '0.5rem',
};

const schoolItem = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '1rem',
  color: '#333',
  padding: '0.5rem 0',
  borderBottom: '1px solid #EDE0F7',
};

const pricingSection = {
  backgroundColor: '#261136',
  padding: '3.5rem 2rem',
  textAlign: 'center',
};

const pricingCtaStyle = {
  display: 'inline-block',
  backgroundColor: '#7B1FA2',
  color: '#FFF',
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 700,
  fontSize: '1rem',
  padding: '12px 28px',
  borderRadius: 999,
  textDecoration: 'none',
  border: '2px solid rgba(255,255,255,0.3)',
};

const faqSectionStyle = {
  padding: '3.5rem 2rem',
  maxWidth: 720,
  margin: '0 auto',
};

const faqItem = {
  borderBottom: '1px solid #EEE',
  paddingBottom: '1.25rem',
  marginBottom: '1.25rem',
};

const faqQuestion = {
  fontFamily: 'Raleway, sans-serif',
  fontWeight: 700,
  fontSize: '1.05rem',
  color: '#261136',
  marginBottom: '0.4rem',
};

const faqAnswer = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '0.97rem',
  color: '#555',
  lineHeight: 1.65,
};

const finalCtaSection = {
  backgroundColor: '#7B1FA2',
  padding: '4rem 2rem',
  textAlign: 'center',
};

const finalCtaHeading = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: 'clamp(1.5rem, 4vw, 2.25rem)',
  fontWeight: 700,
  color: '#FFF',
  margin: '0 0 0.75rem',
};

const finalCtaSub = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '1.1rem',
  color: 'rgba(255,255,255,0.85)',
  margin: '0 0 2rem',
  textAlign: 'center',
};

const internalLinksSection = {
  backgroundColor: '#F9F6FC',
  padding: '2rem',
  textAlign: 'center',
};

const internalLinkStyle = {
  fontFamily: 'Raleway, sans-serif',
  fontSize: '0.95rem',
  color: '#7B1FA2',
  margin: '0 0.75rem',
  textDecoration: 'underline',
};

// ─── Component ────────────────────────────────────────────────────────────────

const Students = () => (
  <>
    <SEO
      title="Student Storage for SF & Tacoma Colleges | Bins Storage"
      description="Summer storage for students at UCSF, SF State, USF, UC Berkeley, UW Tacoma & PLU. We pick up at your dorm, store your stuff safely, and deliver it back for fall. Reserve in minutes."
      path="/students"
      schema={combinedSchema}
    />

    {/* HERO */}
    <section style={heroStyle}>
      <h1 style={heroHeadingStyle}>Student Storage – San Francisco &amp; Tacoma</h1>
      <p style={heroSubStyle}>
        Going home for the summer? Studying abroad? Moving dorms? We'll pick up
        your stuff, store it safely, and deliver it back when you're ready for fall.
      </p>
      <div style={ctaRow}>
        <a href="/Download" style={ctaPrimary} data-cta="students-hero-reserve">
          Reserve Summer Storage
        </a>
        <a href="#how-it-works" style={ctaSecondary} data-cta="students-hero-hiw">
          See How It Works
        </a>
      </div>
      <p style={trustLine}>
        Serving UCSF, SF State, USF, UC Berkeley, UW Tacoma, PLU &amp; more.
      </p>
    </section>

    {/* BENEFITS */}
    <section style={{ backgroundColor: '#FFF', padding: '3.5rem 2rem' }}>
      <div style={sectionStyle}>
        <h2 style={sectionHeadingStyle}>Built for the Student Schedule</h2>
        <div style={cardsGrid}>
          <div style={card}>
            <div style={cardTitle}>🚗 No Car Needed</div>
            <div style={cardBody}>
              You don't need to rent a truck or bribe a friend with a pickup. We
              come to your dorm or apartment.
            </div>
          </div>
          <div style={card}>
            <div style={cardTitle}>💰 Flexible Summer Pricing</div>
            <div style={cardBody}>
              Store from May through August, or longer. Pay only for the unit size
              you need.
            </div>
          </div>
          <div style={card}>
            <div style={cardTitle}>📅 Easy Move-Out &amp; Move-In</div>
            <div style={cardBody}>
              Schedule a pickup on your move-out day. Schedule a delivery on your
              move-in day. That's it.
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* HOW IT WORKS */}
    <section id="how-it-works" style={{ backgroundColor: '#F9F6FC', padding: '3.5rem 2rem' }}>
      <div style={sectionStyle}>
        <h2 style={sectionHeadingStyle}>How Student Storage with Bins Works</h2>
        <div style={cardsGrid}>
          {[
            { n: '1', t: 'Reserve before finals', b: 'Pick a pickup window that works with your move-out date.' },
            { n: '2', t: 'We come to your dorm', b: 'Our team picks up at your building – elevators or walk-ups, no problem.' },
            { n: '3', t: 'We store it all summer', b: 'Climate-controlled, insured, 24/7 monitored.' },
            { n: '4', t: 'We deliver for fall', b: 'Schedule delivery to your new dorm, apartment, or house. Done.' },
          ].map(({ n, t, b }) => (
            <div key={n} style={card}>
              <div style={stepNumber}>{n}</div>
              <div style={cardTitle}>{t}</div>
              <div style={cardBody}>{b}</div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* SCHOOLS */}
    <section style={schoolsSection}>
      <div style={schoolsInner}>
        <h2 style={sectionHeadingStyle}>Colleges in Our Service Area</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem', marginTop: '2rem' }}>
          <div style={schoolGroup}>
            <div style={schoolGroupHeading}>San Francisco Bay Area</div>
            <ul style={schoolList}>
              {['UC San Francisco (UCSF)', 'San Francisco State University (SF State)', 'University of San Francisco (USF)', 'UC Berkeley', 'Stanford (on request)', 'Academy of Art University', 'California College of the Arts'].map(s => (
                <li key={s} style={schoolItem}>{s}</li>
              ))}
            </ul>
          </div>
          <div style={schoolGroup}>
            <div style={schoolGroupHeading}>Tacoma / South Sound</div>
            <ul style={schoolList}>
              {['University of Washington Tacoma (UW Tacoma)', 'Pacific Lutheran University (PLU)', 'University of Puget Sound'].map(s => (
                <li key={s} style={schoolItem}>{s}</li>
              ))}
            </ul>
          </div>
        </div>
        <p style={{ fontFamily: 'Raleway, sans-serif', fontSize: '0.95rem', color: '#666', marginTop: '1.5rem', textAlign: 'center' }}>
          Don't see your school?{' '}
          <a href="/Contact" style={{ color: '#7B1FA2' }}>Contact us</a> – we likely cover it.
        </p>
      </div>
    </section>

    {/* USE CASES */}
    <section style={{ backgroundColor: '#FFF', padding: '3.5rem 2rem' }}>
      <div style={sectionStyle}>
        <h2 style={sectionHeadingStyle}>When Students Use Bins</h2>
        <div style={cardsGrid}>
          {[
            { t: '☀️ Summer Break', b: "Going home but can't fit everything on the plane? We'll hold your stuff until fall." },
            { t: '✈️ Study Abroad', b: "A semester in Madrid doesn't have to mean moving everything back home. Store it cheap." },
            { t: '🏠 Dorm-to-Apartment Moves', b: 'Ending your dorm lease a week before your apartment lease starts? We bridge the gap.' },
            { t: '💼 Mid-Year Internships', b: 'Summer internship in another city? We store, you come back, we deliver.' },
          ].map(({ t, b }) => (
            <div key={t} style={card}>
              <div style={cardTitle}>{t}</div>
              <div style={cardBody}>{b}</div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* PRICING */}
    <section style={pricingSection}>
      <h2 style={{ fontFamily: 'Raleway, sans-serif', fontSize: '2rem', fontWeight: 700, color: '#FFF', marginBottom: '0.75rem' }}>
        Affordable Student Pricing
      </h2>
      <p style={{ fontFamily: 'Raleway, sans-serif', fontSize: '1.05rem', color: '#AAB5E0', maxWidth: 560, margin: '0 auto 2rem', lineHeight: 1.6 }}>
        A typical student's worth of belongings (boxes, mini-fridge, bedding, etc.)
        usually fits in a small unit at a budget-friendly rate. Compare pricing
        across facilities on the reserve page.
      </p>
      <a href="/Download" style={pricingCtaStyle} data-cta="students-pricing-reserve">
        Compare Student Storage Rates →
      </a>
    </section>

    {/* FAQ */}
    <section style={faqSectionStyle}>
      <h2 style={{ ...sectionHeadingStyle, textAlign: 'left', marginBottom: '1.75rem' }}>
        Student FAQ
      </h2>
      {studentFaqs.map(({ q, a }) => (
        <div key={q} style={faqItem}>
          <div style={faqQuestion}>{q}</div>
          <div style={faqAnswer}>{a}</div>
        </div>
      ))}
    </section>

    {/* FINAL CTA */}
    <section style={finalCtaSection}>
      <h2 style={finalCtaHeading}>Lock in Your Summer Storage Before Finals.</h2>
      <p style={finalCtaSub}>Reserve your unit in minutes. Pick up on your move-out day.</p>
      <a href="/Download" style={ctaPrimary} data-cta="students-final-reserve">
        Reserve Now
      </a>
    </section>

    {/* INTERNAL LINKS */}
    <section style={internalLinksSection}>
      <p style={{ fontFamily: 'Raleway, sans-serif', color: '#666', fontSize: '0.9rem', margin: '0 0 0.75rem', textAlign: 'center' }}>
        Explore more
      </p>
      <a href="/san-francisco" style={internalLinkStyle}>SF Bay Area Storage</a>
      <a href="/tacoma" style={internalLinkStyle}>Tacoma Storage</a>
      <a href="/apartment-storage" style={internalLinkStyle}>Apartment Storage</a>
      <a href="/FAQ" style={internalLinkStyle}>FAQ</a>
      <a href="/About" style={internalLinkStyle}>About</a>
    </section>
  </>
);

export default Students;
